import { useModals } from 'almanac-ui/Modal'
import { Assignee, PostStatusPropertyValue, PropertyValue } from 'api/clients/PropertyClient'
import { Button } from 'blaze-ui/Button'
import { DateTimePicker } from 'blaze-ui/DateTimePicker/DateTimePicker'
import { Calendar2, CalendarTrash } from 'blaze-ui/icons'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { UserIntent } from 'components/PostStatusDropdown/Types'
import { usePostStatusDropdownState } from 'components/PostStatusDropdown/usePostStatusDropdownState'
import { usePostProperties } from 'components/ArticlePostPreview/usePostProperties'
import dayjs, { Dayjs } from 'dayjs'
import { useMemo, useState } from 'react'
import Styles from './DateTimePickerEdit.module.scss'

export const DateTimePickerEdit = ({
  selectedPropertyValues,
  onSelect,
  onClose,
  assignee,
}: {
  assignee: Assignee
  selectedPropertyValues: PropertyValue[]
  onSelect: (date: string) => void
  onClose: () => void
}) => {
  const [localDate, setLocalDate] = useState<string>(selectedPropertyValues[0]?.value || dayjs().hour(12).toISOString())
  const { closeModal } = useModals()
  const { executeAction, state, isPostable, hasIntegration } = usePostStatusDropdownState({
    assignee,
  })
  const { status } = usePostProperties(assignee)

  const handleClose = () => {
    onSelect(localDate)
    closeModal()
  }

  const handleSubmit = async () => {
    if (state.status !== PostStatusPropertyValue.Draft) {
      showToastMessage('Time and date set', 'success')
      onSelect(localDate)
      return
    }

    const intent = isPostable && hasIntegration ? UserIntent.ScheduleAutoPost : UserIntent.ScheduleReminder

    await executeAction(intent, localDate)
    handleClose()
  }

  const hasDate = selectedPropertyValues[0]?.value && selectedPropertyValues[0].value.trim() !== ''
  const isReminder = status?.value === PostStatusPropertyValue.ReminderScheduled || !hasIntegration

  const setDateCopy = useMemo(() => {
    if (isReminder) {
      return 'Set Reminder'
    }

    return hasDate ? 'Reschedule Post' : 'Schedule Post'
  }, [isReminder, hasDate])

  const removeDateCopy = isReminder ? 'Remove Reminder' : 'Unschedule'

  return (
    <DateTimePicker
      inline
      dateTime={dayjs(localDate)}
      setDateTime={(date) => setLocalDate((date as Dayjs).toISOString())}
      wrapperClassName={Styles.wrapper}
      actions={() => (
        <>
          <Button
            variant="primary"
            frontIcon={Calendar2}
            onPress={() => {
              onClose()
              handleSubmit()
            }}
          >
            {setDateCopy}
          </Button>
          {hasDate ? (
            <Button
              variant="tertiary"
              frontIcon={CalendarTrash}
              onPress={() => {
                onClose()
                executeAction(UserIntent.Unschedule)
              }}
            >
              {removeDateCopy}
            </Button>
          ) : null}
        </>
      )}
    />
  )
}
